import { getLocalPrices } from "../shared/currency";
import type { CampaignType, Currency, ExchangeRates, Platform, Prices, SelectedTalent, Talent } from "../shared/types";

export function getDefaultGuidePrice(
    priceObj: Prices | undefined,
    platform: Platform | undefined,
    campaignType: CampaignType,
) {
    priceObj = priceObj || {};

    if (campaignType === "Song") {
        return priceObj[("songPrice" + platform) as keyof Prices] || 0;
    }
    if (platform === "TikTok" && priceObj.brandPriceTikTok) {
        return priceObj.brandPriceTikTok;
    }
    if (platform === "Instagram" && priceObj.brandPriceInstagramReel) {
        return priceObj.brandPriceInstagramReel;
    }
    if (platform === "YouTube" && priceObj.brandPriceYouTubeIntegration) {
        return priceObj.brandPriceYouTubeIntegration;
    }
    return 0;
}

/** Stamp localPrices, defaultPrice, and list custom price/notes onto each talent. */
export function calculateTalentWithPrices(
    talent: Talent[],
    exchangeRates: ExchangeRates,
    priceFilter: { currency: Currency; campaignPlatform?: Platform; campaignType: CampaignType },
    listTalent: SelectedTalent,
): Talent[] {
    return talent.map((tal) => {
        const localPrices = getLocalPrices(tal, exchangeRates, priceFilter.currency)
        const defaultPrice = getDefaultGuidePrice(
            localPrices || {},
            priceFilter.campaignPlatform,
            priceFilter.campaignType,
        )
        return {
            ...tal,
            defaultPrice,
            listCustomPrice: listTalent[tal.id]?.price || null,
            localPrices,
            listNotes: listTalent[tal.id]?.notes || null,
        } as Talent
    })
}

export type GuidePriceLine = {
    platform: Platform
    label: string
    price?: number | null
}

const BRAND_GUIDE_LINES: Record<Platform, { label: string; key: keyof Prices }[]> = {
    TikTok: [{ label: "Guide Price", key: "brandPriceTikTok" }],
    Instagram: [
        { label: "Grid Post", key: "brandPriceInstagramPost" },
        { label: "Reel", key: "brandPriceInstagramReel" },
        { label: "3-Frame Story", key: "brandPriceInstagramStory" },
    ],
    YouTube: [
        { label: "Integration Price", key: "brandPriceYouTubeIntegration" },
        { label: "Preroll Price", key: "brandPriceYouTubePreroll" },
        { label: "Short Price", key: "brandPriceYouTubeShort" },
    ],
}

const SONG_GUIDE_KEY: Record<Platform, keyof Prices> = {
    TikTok: "songPriceTikTok",
    Instagram: "songPriceInstagram",
    YouTube: "songPriceYouTube",
}

/** Guide rates for the campaign's platforms and type. */
export function guidePriceLines(
    prices: Prices | undefined,
    platforms: Platform[],
    campaignType: CampaignType,
): GuidePriceLine[] {
    const priceObj = prices || {}
    if (campaignType === "Song") {
        return platforms.map((platform) => ({
            platform,
            label: "Song Price",
            price: priceObj[SONG_GUIDE_KEY[platform]],
        }))
    }
    return platforms.flatMap((platform) =>
        (BRAND_GUIDE_LINES[platform] || []).map((line) => ({
            platform,
            label: line.label,
            price: priceObj[line.key],
        })),
    )
}
